import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editedEvent, setEditedEvent] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [applications, setApplications] = useState([]);
  const [viewingApplications, setViewingApplications] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const token = localStorage.getItem("token");
    if (!token) {setTimeout(() => {toast.error("No token found. Please log in.");}, 0); return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/adminDashboard", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setEvents(data.events || []);
        if (!toast.isActive("admin-events-loaded")) {toast.success("Events loaded successfully", {toastId: "admin-events-loaded"});}
      } else {
        toast.error(data.message || "Failed to fetch events");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching events");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (eventId, eventData) => {
    setEditingEventId(eventId);
    setEditedEvent({ ...eventData, originalEventName: eventData.eventName });
  };

  const handleInputChange = (field, value) => {
    const parsedValue = field === "eventIsActive" ? value === "true" : value;
    setEditedEvent((prev) => ({ ...prev, [field]: parsedValue }));
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found. Please log in.");
    const payload = {
      EventName: editedEvent.originalEventName,
      updatedEventName: editedEvent.eventName,
      updatedEventDescription: editedEvent.eventDescription,
      updatedParticipantQTY: editedEvent.eventParticipantQty,
      isActive: editedEvent.eventIsActive,
      eventPhoto: editedEvent.eventPhoto,
    };
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/editEventsAdmin", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        setEvents((prev) =>
          prev.map((e) => (e._id === editingEventId ? { ...e, ...editedEvent } : e))
        );
        toast.success("Event updated successfully");
        setEditingEventId(null);
      } else {
        toast.error(data.message || "Failed to update event");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating event");
    }
  };

  const handleCancel = () => {
    setEditingEventId(null);
    setEditedEvent({});
  };

  const fetchApplications = async (eventName) => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found. Please log in.");
    setLoading(true);
    try {
      const response = await fetch(`https://tbido-website-api.azurewebsites.net/getApplicationsEvents/${encodeURIComponent(eventName)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setApplications(data.applications || []);
        setSelectedEvent(eventName);
        setViewingApplications(true);
        toast.success(`Applications for ${eventName} loaded`);
      } else {
        toast.error(data.message || `Failed to fetch applications`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching applications");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found.");
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/acceptApplication", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ _id: applicationId }),
      });
      if (response.ok) {
        setApplications((prev) =>
          prev.map((a) => (a._id === applicationId ? { ...a, status: "Approved" } : a))
        );
        toast.success("Application approved");
      } else {
        const data = await response.json();
        toast.error(data.error || "Failed to approve");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error approving application");
    }
  };

  const handleDecline = async (applicationId) => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found.");
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/declineApplication", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ _id: applicationId }),
      });
      if (response.ok) {
        setApplications((prev) =>
          prev.map((a) => (a._id === applicationId ? { ...a, status: "Declined" } : a))
        );
        toast.success("Application declined");
      } else {
        const data = await response.json();
        toast.error(data.error || "Failed to decline");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error declining application");
    }
  };

  const closeApplicationsView = () => {
    setViewingApplications(false);
    setSelectedEvent(null);
    setApplications([]);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
    setTimeout(() => {
      window.close(); // Close the tab
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {viewingApplications ? (
        // Applications View
        <>
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Applications <span className="font-normal">for {selectedEvent}</span>
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Review and manage applications for this event
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={closeApplicationsView}
                    className="px-6 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 shadow-md"
                  >
                    ← Back to Events
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-md"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-red-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
                </div>
                <p className="mt-4 text-gray-600 font-medium">Loading applications...</p>
              </div>
            ) : applications.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-600 to-blue-800 text-white">
                        <th className="px-6 py-4 text-left text-sm font-semibold">Startup Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Contact</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {applications.map((app) => (
                        <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {app.startUpName}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {app.emailAddress}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {app.contactNumber}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              app.status === 'Approved' ? 'bg-green-100 text-green-800' :
                              app.status === 'Declined' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {app.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center gap-2">
                              <button 
                                onClick={() => handleApprove(app._id)}
                                disabled={app.status === 'Approved'}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Approve
                              </button>
                              <button 
                                onClick={() => handleDecline(app._id)}
                                disabled={app.status === 'Declined'}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Decline
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-600">No applications found for this event.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        // Events Dashboard View
        <>
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Event <span className="font-normal">Dashboard</span>
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    View the current status and updates of your events and applications
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/admin-nav')}
                    className="px-4 sm:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-all duration-200 shadow-sm"
                  >
                    ← Back to Nav
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-md"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-red-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
                </div>
                <p className="mt-4 text-gray-600 font-medium">Loading events...</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-600 to-blue-800 text-white">
                        <th className="px-6 py-4 text-left text-sm font-semibold">Event Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Description</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Max Qty</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Active</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {events.map((event) => (
                        <tr key={event._id} className="hover:bg-gray-50 transition-colors">
                          {editingEventId === event._id ? (
                            <>
                              <td className="px-6 py-4">
                                <input 
                                  value={editedEvent.eventName}
                                  onChange={(e) => handleInputChange("eventName", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <textarea 
                                  value={editedEvent.eventDescription}
                                  onChange={(e) => handleInputChange("eventDescription", e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <input 
                                  type="number"
                                  value={editedEvent.eventParticipantQty}
                                  onChange={(e) => handleInputChange("eventParticipantQty", e.target.value)}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                              </td>
                              <td className="px-6 py-4 text-center">
                                <select 
                                  value={editedEvent.eventIsActive}
                                  onChange={(e) => handleInputChange("eventIsActive", e.target.value)}
                                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                >
                                  <option value={true}>Yes</option>
                                  <option value={false}>No</option>
                                </select>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-center gap-2">
                                  <button 
                                    onClick={handleSave}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                                  >
                                    Save
                                  </button>
                                  <button 
                                    onClick={handleCancel}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {event.eventName}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                                <div className="line-clamp-2">{event.eventDescription}</div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700 text-center">
                                {event.eventParticipantQty}
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  event.eventIsActive 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {event.eventIsActive ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex justify-center gap-2">
                                  <button 
                                    onClick={() => handleEdit(event._id, event)}
                                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors"
                                  >
                                    Edit Event
                                  </button>
                                  <button 
                                    onClick={() => fetchApplications(event.eventName)}
                                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-blue-900 transition-colors"
                                  >
                                    View Applications
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;